import * as React from 'react';
import { Alert, Button, PageSection, Skeleton, Tab, TabTitleText, Tabs, Title } from '@patternfly/react-core';
import { useHistory, useLocation } from 'react-router-dom';
import {
  CHG_PRACTICE_WINDOWS,
  isChgPracticeWindow,
  type ChgPracticeWindow,
  type Payload,
  type ViewState,
} from '../../../shared/riskregister/types';
import { relaxedBase } from '../../../shared/riskregister/filters';
import { AsOfControl } from './components/AsOfControl';
import { DecisionsPanel } from './components/DecisionsPanel';
import { DeliveredPanel } from './components/DeliveredPanel';
import { FreshnessBar } from './components/FreshnessBar';
import { ItemsTable } from './components/ItemsTable';
import { NeedsAttentionPanel } from './components/NeedsAttentionPanel';
import { PositionTiles } from './components/PositionTiles';
import { AccountabilityTable } from './components/AccountabilityTable';
import { BottlenecksPanel } from './components/BottlenecksPanel';
import { MonthlyTable } from './components/MonthlyTable';
import { RecentPanel } from './components/RecentPanel';
import { ReviewQueuePanel } from './components/ReviewQueuePanel';
import { Definitions } from './components/Definitions';
import { TrendPanel } from './components/TrendPanel';
import { ViewControls } from './components/ViewControls';
import { AreaRiskChart } from './components/executive/AreaRiskChart';
import { RiskMixChart } from './components/executive/RiskMixChart';
import { RiskTrajectoryChart } from './components/executive/RiskTrajectoryChart';
import { ProductDeliveryChart } from './components/product/ProductDeliveryChart';
import { ProductInitiativeTable } from './components/product/ProductInitiativeTable';
import { ProductSummary } from './components/product/ProductSummary';
import { SectionCard } from './common/SectionCard';
import { downloadRiskRegisterCsv, downloadRiskRegisterExcel } from './export';
import { useFilteredRows } from './hooks/useFilteredRows';
import { useRiskRegister } from './hooks/useRiskRegister';
import { useViewState } from './hooks/useViewState';
import { withAsOf } from '../../../shared/riskregister/viewState';
import {
  COLLAPSIBLE_SECTION_IDS,
  PICK_SECTION,
  RISK_REGISTER_TABS,
  type RiskRegisterTab,
  sectionsForTab,
  tabFromSearch,
  withTab,
} from './tabs';
import './RiskRegister.css';

const LoadingPage: React.FunctionComponent = () => (
  <PageSection className="risk-register" aria-busy="true">
    <header className="risk-register__header">
      <Title headingLevel="h1">Risk Register</Title>
      <p>Fetching the live Jira register, ownership mappings, and change evidence.</p>
    </header>
    <div className="risk-register__loading" aria-live="polite">
      <Skeleton width="18rem" />
      <Skeleton width="100%" height="3rem" />
      <Skeleton width="100%" height="15rem" />
      <span>Loading the Risk Register. A cold load can take up to ten seconds.</span>
    </div>
  </PageSection>
);

interface RiskRegisterPageProps {
  payload: Payload;
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
  onReset: () => void;
  onRefresh: () => Promise<void>;
  onAsOfChange: (asOf: string | undefined) => void;
  chgWindow: ChgPracticeWindow;
  onChgWindowChange: (window: ChgPracticeWindow) => void;
  isRefreshing: boolean;
  activeTab: RiskRegisterTab;
  onTabChange: (tab: RiskRegisterTab) => void;
}

const RiskRegisterPage: React.FunctionComponent<RiskRegisterPageProps> = ({
  payload,
  state,
  onChange,
  onReset,
  onRefresh,
  onAsOfChange,
  chgWindow,
  onChgWindowChange,
  isRefreshing,
  activeTab,
  onTabChange,
}) => {
  const { rows, groups } = useFilteredRows(payload, state);
  const relaxedRows = React.useMemo(() => relaxedBase(payload.rows, state, payload), [payload, state]);
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set(COLLAPSIBLE_SECTION_IDS));
  const [isDeliveredItemsExpanded, setIsDeliveredItemsExpanded] = React.useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const isHistorical = payload.as_of !== today;

  const setExpandedSection = React.useCallback((id: string, isExpanded: boolean) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (isExpanded) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const activeSections = sectionsForTab(activeTab);
  const activeTabDefinition = RISK_REGISTER_TABS.find((tab) => tab.id === activeTab);

  // Collapse all / Expand all only ever touches the tab on screen, so the label has to read the
  // state of that tab alone. The nested Delivered items disclosure counts only while the tab
  // holding the Delivered section is showing.
  const hasDeliveredSection = activeSections.some((section) => section.id === 'delivered');
  const isEverythingCollapsed = activeSections.every((section) => !expanded.has(section.id))
    && !(hasDeliveredSection && isDeliveredItemsExpanded);

  const toggleAllSections = React.useCallback(() => {
    const shouldExpand = isEverythingCollapsed;
    setExpanded((current) => {
      const next = new Set(current);
      activeSections.forEach((section) => {
        if (shouldExpand) next.add(section.id);
        else next.delete(section.id);
      });
      return next;
    });
    if (hasDeliveredSection) setIsDeliveredItemsExpanded(shouldExpand);
  }, [activeSections, hasDeliveredSection, isEverythingCollapsed]);

  // The one place a section id maps to what it renders. Only the active tab's sections are
  // asked for, so panels on the other tabs never mount and never run their row-level work.
  const sectionContent = (id: string): React.ReactNode => {
    switch (id) {
      case 'needs-attention':
        return <NeedsAttentionPanel payload={payload} rows={rows} />;
      case 'queue':
        return <ReviewQueuePanel payload={payload} state={state} onChange={onChange} />;
      case 'recent':
        return <RecentPanel payload={payload} state={state} onChange={onChange} />;
      case 'items':
        return <ItemsTable payload={payload} groups={groups} sort={state.sort} onSort={(sort) => onChange({ sort })} />;
      case 'definitions':
        return <Definitions payload={payload} />;
      case 'position':
        return <PositionTiles payload={payload} rows={rows} state={state} />;
      case 'trajectory':
        return <RiskTrajectoryChart payload={payload} rows={relaxedRows} />;
      case 'risk-mix':
        return <RiskMixChart payload={payload} rows={relaxedRows} />;
      case 'area-risk':
        return <AreaRiskChart payload={payload} rows={relaxedRows} />;
      case 'delivered':
        return (
          <DeliveredPanel
            payload={payload}
            rows={relaxedRows}
            state={state}
            onChange={onChange}
            chgWindow={chgWindow}
            onChgWindowChange={onChgWindowChange}
            isDeliveredItemsExpanded={isDeliveredItemsExpanded}
            onDeliveredItemsExpandedChange={setIsDeliveredItemsExpanded}
          />
        );
      case 'decisions':
        return <DecisionsPanel payload={payload} rows={relaxedRows} state={state} onChange={onChange} />;
      case 'trend':
        return <TrendPanel payload={payload} rows={relaxedRows} state={state} onChange={onChange} />;
      case 'bottlenecks':
        return <BottlenecksPanel payload={payload} state={state} />;
      case 'accountability':
        return <AccountabilityTable payload={payload} state={state} onChange={onChange} />;
      case 'monthly':
        return <MonthlyTable payload={payload} state={state} onChange={onChange} />;
      case 'product-summary':
        return <ProductSummary payload={payload} rows={relaxedRows} state={state} />;
      case 'product-initiatives':
        return <ProductInitiativeTable payload={payload} rows={relaxedRows} state={state} onChange={onChange} />;
      case 'product-delivery':
        return <ProductDeliveryChart payload={payload} rows={relaxedRows} state={state} />;
      default:
        return null;
    }
  };

  return (
    <PageSection className="risk-register">
      <Tabs
        className="risk-register__tabs"
        activeKey={activeTab}
        onSelect={(_event, key) => onTabChange(key as RiskRegisterTab)}
        aria-label="Risk Register views"
        isBox
        isFilled
      >
        {RISK_REGISTER_TABS.map((tab) => (
          <Tab key={tab.id} eventKey={tab.id} title={<TabTitleText>{tab.label}</TabTitleText>} />
        ))}
      </Tabs>

      <header className="risk-register__header">
        <div>
          <Title headingLevel="h1" size="xl">Risk Register</Title>
          <p>Current risks and delivery signals from Jira, grouped for leadership review.</p>
        </div>
        <div className="risk-register__header-actions">
          <Button variant="secondary" onClick={toggleAllSections}>
            {isEverythingCollapsed ? 'Expand all' : 'Collapse all'}
          </Button>
          <span className={`risk-register__as-of${isHistorical ? ' risk-register__as-of--historical' : ''}`}>
            As of {payload.as_of}{isHistorical && ' (historical view)'}
          </span>
        </div>
      </header>

      <FreshnessBar payload={payload} isRefreshing={isRefreshing} />
      {isHistorical && (
        <p className="risk-register__historical-note" role="status">
          Historical view: effective-dated platform and initiative mappings and month-end state are reconstructed.
          Risk score and tier, category, ownership, delivery evidence, and other Jira fields remain as of today.
        </p>
      )}

      {/* One slim toolbar sits between the tab bar and the tab's own sections, so a tab switch
          lands on that tab's content rather than on a screen of filter controls. The controls
          themselves live in the pop-up the Filters button opens. The toolbar carries id="pick"
          exactly once in the document even though every tab shows it, and it is one piece of
          state: the same `state` and the same handlers on every tab, so switching tabs never
          resets or forks the filters. */}
      {activeTabDefinition && <p className="risk-register__tab-subtitle">{activeTabDefinition.subtitle}</p>}

      <div id={PICK_SECTION.id} className="risk-register__toolbar-bar">
        <ViewControls
          payload={payload}
          state={state}
          onChange={onChange}
          onReset={onReset}
          onRefresh={onRefresh}
          onExport={() => downloadRiskRegisterCsv(payload, rows)}
          onExportExcel={() => void downloadRiskRegisterExcel(payload, rows)}
          isRefreshing={isRefreshing}
          asOfControl={<AsOfControl asOf={payload.as_of} today={today} onChange={onAsOfChange} />}
        />
      </div>

      {activeSections.map((section) => (
        <SectionCard
          key={section.id}
          id={section.id}
          title={section.title}
          hint={section.hint}
          isExpanded={expanded.has(section.id)}
          onToggle={(isExpanded) => setExpandedSection(section.id, isExpanded)}
        >
          {sectionContent(section.id)}
        </SectionCard>
      ))}
    </PageSection>
  );
};

export const RiskRegister: React.FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const asOf = React.useMemo(
    () => new URLSearchParams(location.search).get('asOf') || undefined,
    [location.search],
  );
  const chgWindow = React.useMemo(() => {
    const value = Number(new URLSearchParams(location.search).get('chgWindow'));
    return isChgPracticeWindow(value) ? value : CHG_PRACTICE_WINDOWS[0];
  }, [location.search]);
  const activeTab = React.useMemo(() => tabFromSearch(location.search), [location.search]);
  const setAsOf = React.useCallback((nextAsOf: string | undefined) => {
    history.replace({
      pathname: location.pathname,
      search: withAsOf(location.search, nextAsOf),
    });
  }, [history, location.pathname, location.search]);
  const setChgWindow = React.useCallback(
    (nextChgWindow: ChgPracticeWindow) => {
      const params = new URLSearchParams(location.search);
      if (nextChgWindow === CHG_PRACTICE_WINDOWS[0]) params.delete('chgWindow');
      else params.set('chgWindow', String(nextChgWindow));
      const search = params.toString();
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    },
    [history, location.pathname, location.search],
  );
  const setActiveTab = React.useCallback(
    (nextTab: RiskRegisterTab) => {
      history.replace({ pathname: location.pathname, search: withTab(location.search, nextTab) });
    },
    [history, location.pathname, location.search],
  );
  const {
    data: payload,
    error,
    isLoading,
    refetch,
    refresh,
    isRefreshing,
    refreshError,
  } = useRiskRegister(asOf, chgWindow);
  const { state, setViewState, resetViewState } = useViewState(payload);

  if (error && !payload) {
    return (
      <PageSection className="risk-register">
        <header className="risk-register__header">
          <Title headingLevel="h1">Risk Register</Title>
          <p>The register could not be loaded.</p>
        </header>
        <Alert
          variant="danger"
          title="Unable to load the Risk Register"
          actionLinks={<Button variant="link" onClick={() => void refetch()}>Try again</Button>}
        >
          {error.message}
        </Alert>
      </PageSection>
    );
  }

  if (isLoading || !payload || !state) return <LoadingPage />;

  return (
    <>
      {refreshError && (
        <PageSection className="risk-register risk-register__refresh-error">
          <Alert variant="warning" title="The register could not be refreshed">
            {refreshError.message}
          </Alert>
        </PageSection>
      )}
      <RiskRegisterPage
        payload={payload}
        state={state}
        onChange={setViewState}
        onReset={resetViewState}
        onRefresh={refresh}
        onAsOfChange={setAsOf}
        chgWindow={chgWindow}
        onChgWindowChange={setChgWindow}
        isRefreshing={isRefreshing}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </>
  );
};
